import type { ThreeSceneEngine } from '../three-scene-engine.class';
import { SceneNotReadyError } from '../../types/export-errors';
import { verifySceneManifestCfn } from './verify-scene-manifest.cfn';
import { KitchenSceneBuilder } from '../../builders';

export type WaitForSceneReadyOptions = {
	timeoutMs?: number;
};

export const waitForSceneReadyCfn = (
	self: ThreeSceneEngine,
	{ timeoutMs = 60000 }: WaitForSceneReadyOptions = {}
): Promise<void> => {
	return new Promise<void>((resolve, reject) => {
		const getManifest = () => {
			const builder = self.getBuilderAccess('kitchen');
			if (builder instanceof KitchenSceneBuilder) return builder.currentManifestAccess;
			return null;
		};

		const checkBuildError = (): string | null => {
			if (self.isDisposedAccess) return 'engine disposed';
			return self.lastBuildErrorAccess;
		};

		const check = (): boolean => {
			if (self.isBuildingSceneAccess || self.hasPendingUpdateAccess) return false;
			const manifest = getManifest();
			if (!manifest) return false;
			const { ready } = verifySceneManifestCfn(self, manifest);
			return ready;
		};

		// Fast-fail if a build error was already recorded
		const buildError = checkBuildError();
		if (buildError) {
			reject(new SceneNotReadyError(`Scene build failed: ${buildError}`, buildError));
			return;
		}

		if (check()) {
			resolve();
			return;
		}

		let pollHandle: ReturnType<typeof setInterval> | null = null;
		let timedOut = false;
		let stuckPolls = 0;
		let selfHealAttempts = 0;

		const cleanup = (): void => {
			if (pollHandle !== null) {
				clearInterval(pollHandle);
				pollHandle = null;
			}
			self.off('render:complete', onComplete);
			self.off('render:error', onError);
		};

		const onError = (event: { data?: unknown }): void => {
			if (timedOut) return;
			timedOut = true;
			cleanup();
			const payload = event.data as { error?: string } | null | undefined;
			const cause = typeof payload?.error === 'string' ? payload.error : 'unknown build error';
			reject(new SceneNotReadyError(`Scene build failed: ${cause}`, cause));
		};

		const onComplete = (): void => {
			if (timedOut) return;
			// Check build error first — a render:complete may fire even after error
			const err = checkBuildError();
			if (err) {
				timedOut = true;
				cleanup();
				reject(new SceneNotReadyError(`Scene build failed: ${err}`, err));
				return;
			}
			if (check()) {
				cleanup();
				resolve();
			}
		};

		self.on('render:complete', onComplete);
		self.on('render:error', onError);

		// 500ms poll as safety net in case events are missed
		pollHandle = setInterval(() => {
			if (timedOut) return;
			const err = checkBuildError();
			if (err) {
				timedOut = true;
				cleanup();
				reject(new SceneNotReadyError(`Scene build failed: ${err}`, err));
				return;
			}
			if (check()) {
				cleanup();
				resolve();
				return;
			}
			// Self-heal: a build aborted by a competing generation bump leaves the scene
			// incomplete with nothing in flight and no error — recover with one rebuild.
			if (!self.isBuildingSceneAccess && !self.hasPendingUpdateAccess) {
				stuckPolls++;
				// A recovery rebuild can itself be stranded by a late competing bump — retry with spacing
				if (stuckPolls >= 16 && selfHealAttempts < 3) {
					selfHealAttempts++;
					stuckPolls = 0;
					console.warn(`[waitForSceneReady] self-heal attempt ${selfHealAttempts}/3`);
					void (self as unknown as { recomposeFromLastSources: () => Promise<void> }).recomposeFromLastSources().catch((healErr: unknown) => {
						console.warn('[waitForSceneReady] self-heal rebuild failed:', healErr);
					});
				}
			} else {
				stuckPolls = 0;
			}
		}, 500);

		setTimeout(() => {
			if (timedOut) return;
			timedOut = true;
			cleanup();
			const buildErr = checkBuildError();
			if (buildErr) {
				reject(new SceneNotReadyError(`Scene build failed: ${buildErr}`, buildErr));
				return;
			}
			const manifest = getManifest();
			const missingList = manifest
				? verifySceneManifestCfn(self, manifest).missing
				: ['manifest not available'];
			const blockers: Array<string> = [];
			if (self.isBuildingSceneAccess) blockers.push('isBuildingScene=true');
			if (self.hasPendingUpdateAccess) blockers.push('hasPendingUpdate=true');
			if (!manifest) blockers.push('manifest=null');
			reject(new SceneNotReadyError(
				`Scene not ready after ${timeoutMs}ms. Missing: ${missingList.join(', ')}${blockers.length ? ` | blocked-by: ${blockers.join(', ')}` : ''}`
			));
		}, timeoutMs);
	});
};
